import { useAuth } from "context/AuthProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Table,
} from "reactstrap";
import { apiNotificacionesService } from "services/apiNotificacionesService";
const MostrarNotidciaciones = () => {
  const { empresa } = useAuth();
  const navigate = useNavigate();
  const [notificacionesSistema, setNotificacionesSistema] = useState([]);
  const [loadingSistema, setLoadingSistema] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const maxPagesToShow = 5;

  const cargarNotificacionesSistema = useCallback(async () => {
    if (!empresa?.id) {
      return;
    }

    setLoadingSistema(true);
    try {
      const response = await apiNotificacionesService.getAllByEmpresa(empresa.id);
      if (!response.ok) {
        setNotificacionesSistema([]);
        return;
      }
      const data = await response.json();
      setNotificacionesSistema(Array.isArray(data) ? data : []);
    } finally {
      setLoadingSistema(false);
    }
  }, [empresa?.id]);

  useEffect(() => {
    cargarNotificacionesSistema();
  }, [cargarNotificacionesSistema]);

  const totalPages = Math.max(
    1,
    Math.ceil(notificacionesSistema.length / pageSize)
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const notificacionesPaginadas = notificacionesSistema.slice(
    start,
    start + pageSize
  );

  const goToPage = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
  };

  const getVisiblePages = () => {
    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const half = Math.floor(maxPagesToShow / 2);
    let startPage = page - half;
    let endPage = page + half;
    if (maxPagesToShow % 2 === 0) {
      endPage -= 1;
    }
    if (startPage < 1) {
      startPage = 1;
      endPage = maxPagesToShow;
    }
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxPagesToShow + 1;
    }
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const onMarcarLeida = async (notificacionId) => {
    if (!empresa?.id) {
      return;
    }
    const response = await apiNotificacionesService.marcarLeida(
      empresa.id,
      notificacionId
    );
    if (response.ok) {
      setNotificacionesSistema((prev) =>
        prev.map((item) =>
          item.id === notificacionId ? { ...item, leida: true } : item
        )
      );
    }
  };

  const onEliminar = async (notificacionId) => {
    if (!empresa?.id) {
      return;
    }
    const response = await apiNotificacionesService.eliminar(
      empresa.id,
      notificacionId
    );
    if (response.ok) {
      setNotificacionesSistema((prev) =>
        prev.filter((item) => item.id !== notificacionId)
      );
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) {
      return "-";
    }
    const parsed = new Date(fecha);
    if (Number.isNaN(parsed.getTime())) {
      return fecha;
    }
    return parsed.toLocaleString("es-ES");
  };

  const esCategoriaAusencias = (categoria) => {
    if (!categoria) {
      return false;
    }
    const normalizada = String(categoria).toUpperCase();
    return (
      normalizada === "AUSENCIA" ||
      normalizada === "AUSENCIAS" ||
      normalizada === "ASUENCIAS"
    );
  };

  const esCategoriaIncidencias = (categoria) => {
    if (!categoria) {
      return false;
    }
    return String(categoria).trim().toUpperCase() === "INCIDENCIAS";
  };

  const esAusenciaIncidencia = (notificacion) => {
    if (!notificacion) {
      return false;
    }
    return esCategoriaIncidencias(notificacion.categoria);
  };

  const onTramitar = (empleadoId) => {
    if (!empleadoId) {
      return;
    }
    navigate(`/admin/solicitudes/${empleadoId}`);
  };

  return (
    <Card>
      <CardHeader>Notificaciones</CardHeader>
      <CardBody className="p-0">
        <Table
          className="mb-0"
          responsive
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead className="thead-light">
            <tr>
              <th style={{ width: "16%" }}>Fecha</th>
              <th style={{ width: "18%" }}>Titulo</th>
              <th style={{ width: "36%" }}>Mensaje</th>
              <th style={{ width: "30%" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!loadingSistema && notificacionesSistema.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-muted">
                  Sin notificaciones
                </td>
              </tr>
            )}
            {notificacionesPaginadas.map((notificacion) => {
              const esWarning = esAusenciaIncidencia(notificacion);
              return (
                <tr key={notificacion.id} className={esWarning ? "table-warning" : undefined}>

                <td
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                  }}
                >
                  {formatearFecha(notificacion.fecha)}
                </td>
                <td
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                  }}
                >
                  {notificacion.titulo}
                  {esWarning && (
                    <Badge color="warning" className="ml-2">
                      Incidencia
                    </Badge>
                  )}
                </td>
                <td
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                  }}
                >
                  {notificacion.mensaje}
                </td>
                <td>
                  {!esCategoriaAusencias(notificacion.categoria) && (
                    <Button
                      color="danger"
                      size="sm"
                      className="btn-icon-only"
                      onClick={() => onEliminar(notificacion.id)}
                      title="Eliminar"
                      aria-label="Eliminar"
                    >
                      <i className="fas fa-trash" />
                    </Button>
                  )}
                  {esCategoriaAusencias(notificacion.categoria) && (
                    <Button
                      color="primary"
                      size="sm"
                      className="ml-2"
                      onClick={() => onTramitar(notificacion.empleadoId)}
                      title="Tramitar solicitud"
                      aria-label="Tramitar solicitud"
                    >
                      Tramitar
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </Table>
        {notificacionesSistema.length > pageSize && (
          <div className="d-flex justify-content-center p-3">
            <Pagination aria-label="Paginación de notificaciones">
              <PaginationItem disabled={page === 1}>
                <PaginationLink previous onClick={() => goToPage(page - 1)} />
              </PaginationItem>
              {getVisiblePages().map((pageNumber) => (
                  <PaginationItem active={pageNumber === page} key={pageNumber}>
                    <PaginationLink onClick={() => goToPage(pageNumber)}>
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
              ))}
              <PaginationItem disabled={page === totalPages}>
                <PaginationLink next onClick={() => goToPage(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default MostrarNotidciaciones;

